// returns an array of length to-from, containing numbers
// getNumbersArray(4, 12) => [4,5,6,7,8,9,10,11]

import { THOUSANDS_SEPARATOR_REGEX } from './validation';

// to must be larger then from
export const getNumbersArray = (from: number, to: number) => {
    return Array(to - from)
        .fill(0)
        .map((val, index) => index + from);
};

// 1 -> 1.00000 | 0.32 -> 0.32000
export const EXCHANGE_RATE_DECIMAL_VIEW_NUMBER = 5;

// mean 10.99 => 10.99
// 6 times of 9 - 10.999999 => 11
const DEFAULT_DECIMAL_NUMBER_TO_ROUND = 6;

export type DecimalKeys = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
const DECIMAL_MAP = {
    0: 1,
    1: 10,
    2: 100,
    3: 1000,
    4: 10000,
    5: 100000,
    6: 1000000,
    7: 10000000,
    8: 100000000,
    9: 1000000000,
    10: 10000000000,
    11: 100000000000,
    12: 1000000000000
} as const satisfies Record<DecimalKeys, number>;

export const getToFixedNumber = (number?: number, decimal: DecimalKeys = 2): number => {
    if (typeof number !== 'number' || isNaN(number)) return 0;

    const roundedNumber = getNumberRoundedIfLongDecimalSame(number);
    const multiplier = DECIMAL_MAP[decimal];
    return Math.trunc(getNumberRoundedIfLongDecimalSame(roundedNumber * multiplier)) / multiplier;
};

/**
 * Scale `values` proportionally so they sum to `total`, rounded to 2dp, with
 * the rounding remainder assigned to the largest entry.
 *
 * Rounding each row independently leaves the rows short of (or over) the total
 * they were scaled to — 1,000 across three passengers rounds to 333.33 × 3 =
 * 999.99 — so a printed passenger table did not add up to the Amount box above
 * it. The remainder goes to the LARGEST row because a cent is least noticeable
 * there, and because "largest" is stable no matter how the caller sorts the
 * rows afterwards (the flight voucher re-sorts by surname once the amounts are
 * fixed). Ties resolve to the first such row, so the result is deterministic.
 *
 * Returns the values unchanged (coerced to numbers) when there is nothing to
 * scale or nothing to scale against — a zero or negative `total`, or values
 * that do not sum above zero. Callers rely on that: a service with no
 * service-level total must keep the passengers' own figures rather than be
 * forced to zero.
 *
 * Shared by the voucher form and the PDF builder deliberately. They render the
 * same passenger table and their arithmetic has to be identical to the cent —
 * the rows the form stores drive refund selection and BSP ticket matching.
 */
export const scaleToTotalWithRemainder = (values: number[], total: number): number[] => {
    const numbers = values.map((value) => (Number.isFinite(Number(value)) ? Number(value) : 0));
    const sum = numbers.reduce((acc, value) => acc + value, 0);
    if (!Number.isFinite(total) || total === 0) return numbers;
    // Guard on MAGNITUDE, not sign. A negative total is a credit or correction
    // and its breakdown has to carry the same sign — leaving positive rows
    // under a negative total printed a table that contradicted the box above
    // it. A negative scale flips them to match.
    //
    // The epsilon matters: mixed-sign components that ought to cancel can leave
    // a denormal like 5e-17 instead of exact zero, and dividing by that sends
    // every row astronomical. Below half a cent there is nothing meaningful to
    // scale, so the rows stand as they are.
    if (Math.abs(sum) < 0.005) return numbers;

    // Deliberately Math.round, not getToFixedNumber: the latter TRUNCATES, so
    // every row would lose up to a cent and the whole accumulated shortfall
    // would land on the single row that absorbs the remainder — 0.20 on one
    // passenger out of twenty. Rounding keeps each row at its nearest true
    // share and leaves at most a cent or two to place.
    const round2 = (value: number) => Math.round(value * 100) / 100;
    const scale = total / sum;
    const scaled = numbers.map((value) => round2(value * scale));
    const remainder = round2(total - scaled.reduce((acc, value) => acc + value, 0));
    if (!remainder) return scaled;

    // By magnitude, so an all-negative breakdown puts the cent on its biggest
    // row rather than its smallest.
    let largest = 0;
    for (let i = 1; i < scaled.length; i++) {
        if (Math.abs(scaled[i]) > Math.abs(scaled[largest])) largest = i;
    }
    scaled[largest] = round2(scaled[largest] + remainder);
    return scaled;
};

// 1 => '1.00' | 1234567.8 => '1,234,567.80' | 0.12345 => '0.12'
export const taytanFormatNumberToString = (number: number, decimal: DecimalKeys = 2): string => {
    const fixedNumber = getToFixedNumber(number, decimal);

    const numberInSplitArray = fixedNumber.toString().split('.');
    const numberOfDecimal = numberInSplitArray[1]?.length || 0;

    const numberStringAboveDecimal = numberInSplitArray[0].replace(THOUSANDS_SEPARATOR_REGEX, '$1,');
    const numberWithCommas = `${numberStringAboveDecimal}${
        numberInSplitArray[1] ? `.${numberInSplitArray[1]}` : ''
    }`;

    let stringDecimalToAdd = '';
    if (isNumberHaveDecimal(fixedNumber)) {
        if (numberOfDecimal < decimal) {
            stringDecimalToAdd = '0'.repeat(decimal - numberOfDecimal);
        }
    } else {
        if (decimal) {
            stringDecimalToAdd = '.' + '0'.repeat(decimal);
        }
    }
    return numberWithCommas + stringDecimalToAdd;
};

export const formatMoney = (value: number | string | undefined | null, decimal: DecimalKeys = 2): string => {
    if (value === undefined || value === null || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '';
    return taytanFormatNumberToString(num, decimal);
};

export const isNumberHaveDecimal = (number: number) => {
    return number % 1 !== 0;
};

// case 1: number: 10.999999999999
// roundedNumber = (10.999999999999).toFixed(8) ---> '11.00000000' ---> Number('11.00000000') ---> 11
// Math.trunc(roundedNumber) !== Math.trunc(10.999999999999) ---> 11 !== 10 ---> true, so return 11

// case 2: number: 10.9999*8*9999999
// roundedNumber = (10.999989999999).toFixed(8) ---> '10.99999000' ---> Number('10.99999000') ---> 10.99999
// Math.trunc(roundedNumber) !== Math.trunc(10.999989999999) ---> 10 !== 10 ---> false, so return 10.999989999999
export const getNumberRoundedIfLongDecimalSame = (number: number): number => {
    if (isNumberHaveDecimal(number)) {
        // check if number have decimal 10=false 10.3=true
        for (let i = 0; i < DEFAULT_DECIMAL_NUMBER_TO_ROUND + 1; i++) {
            const powersOf10 = DECIMAL_MAP[i as DecimalKeys];
            if (!powersOf10) break;

            const multiple = number * powersOf10;
            const roundedNumber = Number(multiple.toFixed(DEFAULT_DECIMAL_NUMBER_TO_ROUND));
            if (Math.trunc(roundedNumber) !== Math.trunc(multiple)) {
                return roundedNumber / powersOf10;
            }
            // If converting to a number changes the string representation,
            // preserve the precision by re-rounding after scaling down.
            if (i === DEFAULT_DECIMAL_NUMBER_TO_ROUND) {
                const multiFixed = multiple.toFixed(DEFAULT_DECIMAL_NUMBER_TO_ROUND + 1);
                const numericMultiple = Number(multiFixed);

                if (`${numericMultiple}` !== multiFixed) {
                    const dividedValue = numericMultiple / powersOf10;
                    const roundedResult = dividedValue.toFixed(DEFAULT_DECIMAL_NUMBER_TO_ROUND + 1);

                    return Number(roundedResult);
                }
            }
        }
    }
    return number;
};
